import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersApi, companiesApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Users, User, Plus, UploadSimple, MagnifyingGlass, CaretLeft, CaretRight, Buildings, ArrowLeft, Trash, GenderMale, GenderFemale, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';
import BulkUploadDialog from '../../components/BulkUploadDialog';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import BulkDeleteConfirmDialog from '../../components/BulkDeleteConfirmDialog';
import { SingleDuplicateDialog, BulkDuplicateDialog } from '../../components/DuplicateConfirmDialog';
import { validateRut } from '../../utils/rutValidator';
import { Checkbox } from '../../components/ui/checkbox';

const ITEMS_PER_PAGE = 10;

export default function CompanyDetailPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('workers');
  const [showAddWorker, setShowAddWorker] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [addingWorker, setAddingWorker] = useState(false);
  const [deletingWorker, setDeletingWorker] = useState(null);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState(new Set());
  const [showBulkDeleteWorkers, setShowBulkDeleteWorkers] = useState(false);
  const [duplicateWorker, setDuplicateWorker] = useState(null);
  const [pendingWorkerData, setPendingWorkerData] = useState(null);
  const [bulkDuplicates, setBulkDuplicates] = useState(null);
  const [pendingBulkData, setPendingBulkData] = useState(null);
  const [workerForm, setWorkerForm] = useState({
    rut: '', first_name: '', last_name: '', cargo: '', sexo: '', email: '', phone: '', fecha_nacimiento: ''
  });
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [assigningUser, setAssigningUser] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [unassigningUser, setUnassigningUser] = useState(null);

  const fetchCompany = useCallback(async () => {
    try {
      const res = await companiesApi.getById(companyId);
      setCompany(res.data);
    } catch {
      toast.error('Empresa no encontrada');
      navigate('/admin/companies');
    }
  }, [companyId, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const [workersRes, assignedRes] = await Promise.all([
        usersApi.getAll({ company_id: companyId, role: 'worker', page_size: 9999 }),
        companiesApi.getAssignedUsers(companyId)
      ]);
      const workersData = workersRes.data.items || workersRes.data;
      setWorkers(Array.isArray(workersData) ? workersData.filter(u => u.role === 'worker') : []);
      setUsers(assignedRes.data);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompany();
    fetchUsers();
  }, [fetchCompany, fetchUsers]);

  const handleAddWorker = async (e) => {
    e.preventDefault();
    // Validate RUT
    if (workerForm.rut) {
      const rutCheck = validateRut(workerForm.rut);
      if (!rutCheck.valid) {
        toast.error(rutCheck.error);
        return;
      }
      workerForm.rut = rutCheck.formatted;
    }
    setAddingWorker(true);
    try {
      const autoPassword = workerForm.rut ? workerForm.rut.replace(/[^0-9kK]/g, '') : `${workerForm.email.split('@')[0]}123`;
      const submitData = {
        rut: workerForm.rut || null, first_name: workerForm.first_name, last_name: workerForm.last_name,
        cargo: workerForm.cargo || null, sexo: workerForm.sexo || null, email: workerForm.email,
        phone: workerForm.phone || null, fecha_nacimiento: workerForm.fecha_nacimiento || null,
        password: autoPassword, role: 'worker', company_id: companyId
      };
      const checkRes = await usersApi.checkDuplicate({ email: submitData.email, role: 'worker', rut: submitData.rut });
      if (checkRes.data.exists) {
        setDuplicateWorker(checkRes.data.user);
        setPendingWorkerData(submitData);
        setAddingWorker(false);
        return;
      }
      await usersApi.create(submitData);
      toast.success('Trabajador agregado exitosamente');
      setWorkerForm({ rut: '', first_name: '', last_name: '', cargo: '', sexo: '', email: '', phone: '', fecha_nacimiento: '' });
      setShowAddWorker(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar trabajador');
    } finally {
      setAddingWorker(false);
    }
  };

  const handleUpdateDuplicateWorker = async () => {
    if (!duplicateWorker || !pendingWorkerData) return;
    try {
      await usersApi.update(duplicateWorker.id, pendingWorkerData);
      toast.success('Trabajador actualizado exitosamente');
      setDuplicateWorker(null); setPendingWorkerData(null);
      setWorkerForm({ rut: '', first_name: '', last_name: '', cargo: '', sexo: '', email: '', phone: '', fecha_nacimiento: '' });
      setShowAddWorker(false); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al actualizar'); }
  };

  const fetchAvailableUsers = useCallback(async (search) => {
    setLoadingAvailable(true);
    try {
      const res = await companiesApi.getAvailableUsers(companyId, search || undefined);
      setAvailableUsers(res.data);
    } catch {
      toast.error('Error al buscar usuarios');
    } finally {
      setLoadingAvailable(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (showAssignUser) {
      fetchAvailableUsers(userSearchTerm);
    }
  }, [showAssignUser, userSearchTerm, fetchAvailableUsers]);

  const handleAssignUser = async (userId) => {
    setAssigningUser(true);
    try {
      await companiesApi.assignUser(companyId, userId);
      toast.success('Usuario asignado exitosamente');
      fetchUsers();
      fetchAvailableUsers(userSearchTerm);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar usuario');
    } finally {
      setAssigningUser(false);
    }
  };

  const handleUnassignUser = async (userId) => {
    try {
      await companiesApi.unassignUser(companyId, userId);
      toast.success('Usuario desasignado exitosamente');
      setUnassigningUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desasignar usuario');
    }
  };

  const filteredWorkers = workers.filter(w =>
    `${w.first_name} ${w.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (w.rut && w.rut.includes(searchTerm))
  );

  const totalPages = Math.ceil(filteredWorkers.length / ITEMS_PER_PAGE);
  const paginatedWorkers = filteredWorkers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const toggleSelectWorker = (id) => {
    setSelectedWorkerIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAllWorkers = () => {
    const pageIds = paginatedWorkers.map(w => w.id);
    const allSelected = pageIds.every(id => selectedWorkerIds.has(id));
    setSelectedWorkerIds(prev => {
      const next = new Set(prev);
      pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const clearWorkerSelection = () => setSelectedWorkerIds(new Set());

  if (loading || !company) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  const workerRutCheck = validateRut(workerForm.rut);
  const workerRutInvalid = workerForm.rut.trim() !== '' && !workerRutCheck.valid;
  const workerAllFilled = workerForm.rut.trim() && workerForm.first_name.trim() && workerForm.last_name.trim() && workerForm.cargo.trim() && workerForm.sexo && workerForm.email.trim() && workerForm.phone.trim() && workerForm.fecha_nacimiento;
  const workerCanSubmit = Boolean(workerAllFilled) && workerRutCheck.valid && !addingWorker;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="company-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/companies')}
          className="text-[#2B4B8C]/60 hover:text-[#2B4B8C] -ml-2"
          data-testid="back-to-companies"
        >
          <ArrowLeft size={20} className="mr-1" /> Volver
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#2B4B8C]/10 flex items-center justify-center">
          <Buildings size={26} className="text-[#2B4B8C]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">{company.name}</h1>
          <p className="text-[#2B4B8C]/50 text-sm">
            {company.rut || 'Sin RUT'} &middot; {company.is_client ? 'Mandante' : 'Contratista'}
            {company.email && <> &middot; {company.email}</>}
            {company.phone && <> &middot; {company.phone}</>}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#f8fafc] border border-[#2B4B8C]/10">
          <TabsTrigger value="workers" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-workers">
            <Users size={16} className="mr-1.5" /> Trabajadores ({workers.length})
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-users">
            <User size={16} className="mr-1.5" /> Usuarios ({users.length})
          </TabsTrigger>
        </TabsList>

        {/* Workers Tab */}
        <TabsContent value="workers" className="space-y-4 mt-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={18} />
              <Input
                placeholder="Buscar trabajador..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                data-testid="search-workers-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsBulkUploadOpen(true)}
                className="border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#2B4B8C]/5"
                data-testid="company-bulk-upload-workers-btn"
              >
                <UploadSimple size={18} className="mr-2" /> Carga Masiva
              </Button>
              <Button
                onClick={() => setShowAddWorker(!showAddWorker)}
                className="bg-[#2BBAA4] text-black hover:bg-[#239986]"
                data-testid="add-worker-btn"
              >
                <Plus size={18} className="mr-2" /> Agregar Trabajador
              </Button>
            </div>
          </div>

          {/* Add Worker Form */}
          {showAddWorker && (
            <Card className="bg-[#f8fafc] border-[#2B4B8C]/10">
              <CardContent className="p-5">
                <form onSubmit={handleAddWorker} className="space-y-4" data-testid="add-worker-form">
                  <p className="font-medium text-[#2B4B8C]">Nuevo Trabajador</p>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">RUT</Label>
                      <Input value={workerForm.rut} onChange={(e) => setWorkerForm({ ...workerForm, rut: e.target.value })} className={`bg-white text-[#2B4B8C] ${workerRutInvalid ? 'border-red-400 focus-visible:ring-red-400' : 'border-[#2B4B8C]/10'}`} placeholder="12.345.678-9" required data-testid="worker-rut" />
                      {workerRutInvalid && <p className="text-xs text-red-500" data-testid="worker-rut-error">{workerRutCheck.error}</p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Nombre</Label>
                      <Input value={workerForm.first_name} onChange={(e) => setWorkerForm({ ...workerForm, first_name: e.target.value })} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" required data-testid="worker-first-name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Apellido</Label>
                      <Input value={workerForm.last_name} onChange={(e) => setWorkerForm({ ...workerForm, last_name: e.target.value })} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" required data-testid="worker-last-name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Cargo</Label>
                      <Input value={workerForm.cargo} onChange={(e) => setWorkerForm({ ...workerForm, cargo: e.target.value })} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" required data-testid="worker-cargo" />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Sexo</Label>
                      <Select value={workerForm.sexo} onValueChange={(v) => setWorkerForm({ ...workerForm, sexo: v })}>
                        <SelectTrigger className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="worker-sexo">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#2B4B8C]/10">
                          <SelectItem value="M">Masculino</SelectItem>
                          <SelectItem value="F">Femenino</SelectItem>
                          <SelectItem value="Otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Email</Label>
                      <Input type="email" value={workerForm.email} onChange={(e) => setWorkerForm({ ...workerForm, email: e.target.value })} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" required data-testid="worker-email" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Teléfono</Label>
                      <Input value={workerForm.phone} onChange={(e) => setWorkerForm({ ...workerForm, phone: e.target.value })} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" required data-testid="worker-phone" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Fecha de Nacimiento</Label>
                      <Input type="date" value={workerForm.fecha_nacimiento} onChange={(e) => setWorkerForm({ ...workerForm, fecha_nacimiento: e.target.value })} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" required data-testid="worker-fecha-nacimiento" />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button type="button" variant="outline" onClick={() => setShowAddWorker(false)} className="border-[#2B4B8C]/10">Cancelar</Button>
                    <Button type="submit" disabled={!workerCanSubmit} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="submit-add-worker">
                      {addingWorker ? 'Guardando...' : 'Guardar Trabajador'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Gender Stats */}
          {workers.length > 0 && (() => {
            const maleCount = workers.filter(w => w.sexo && ['MASCULINO', 'M', 'HOMBRE', 'H'].includes(w.sexo.toUpperCase())).length;
            const femaleCount = workers.filter(w => w.sexo && ['FEMENINO', 'F', 'MUJER'].includes(w.sexo.toUpperCase())).length;
            const total = workers.length;
            const malePct = total > 0 ? ((maleCount / total) * 100).toFixed(1) : 0;
            const femalePct = total > 0 ? ((femaleCount / total) * 100).toFixed(1) : 0;
            const now = new Date();
            const ages = workers.map(w => {
              if (!w.fecha_nacimiento) return null;
              const birth = new Date(w.fecha_nacimiento);
              if (isNaN(birth.getTime())) return null;
              let age = now.getFullYear() - birth.getFullYear();
              const m = now.getMonth() - birth.getMonth();
              if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
              return age;
            }).filter(a => a !== null);
            const avgAge = ages.length > 0 ? (ages.reduce((s, a) => s + a, 0) / ages.length).toFixed(1) : '-';
            return (
              <div className="grid grid-cols-3 gap-4" data-testid="gender-stats">
                <Card className="bg-white border-[#2B4B8C]/10">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <GenderMale size={22} className="text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-[#2B4B8C]/60">Hombres</p>
                      <p className="text-xl font-bold text-[#2B4B8C]">{maleCount} <span className="text-sm font-normal text-[#2B4B8C]/50">({malePct}%)</span></p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border-[#2B4B8C]/10">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
                      <GenderFemale size={22} className="text-pink-500" />
                    </div>
                    <div>
                      <p className="text-sm text-[#2B4B8C]/60">Mujeres</p>
                      <p className="text-xl font-bold text-[#2B4B8C]">{femaleCount} <span className="text-sm font-normal text-[#2B4B8C]/50">({femalePct}%)</span></p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border-[#2B4B8C]/10">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <CalendarBlank size={22} className="text-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm text-[#2B4B8C]/60">Edad Promedio del Personal</p>
                      <p className="text-xl font-bold text-[#2B4B8C]">{avgAge} <span className="text-sm font-normal text-[#2B4B8C]/50">años</span></p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Bulk Delete Bar for Workers */}
          {selectedWorkerIds.size > 0 && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200 animate-fade-in" data-testid="bulk-delete-bar-workers">
              <span className="text-sm font-medium text-red-700">
                {selectedWorkerIds.size} trabajador(es) seleccionado(s)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearWorkerSelection} className="border-red-200 text-red-600 hover:bg-red-50 h-8" data-testid="clear-selection-workers-btn">
                  Deseleccionar
                </Button>
                <Button size="sm" onClick={() => setShowBulkDeleteWorkers(true)} className="bg-red-500 text-white hover:bg-red-600 h-8" data-testid="bulk-delete-workers-btn">
                  <Trash size={14} className="mr-1" /> Eliminar Seleccionados
                </Button>
              </div>
            </div>
          )}

          {/* Workers Table */}
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={paginatedWorkers.length > 0 && paginatedWorkers.every(w => selectedWorkerIds.has(w.id))}
                        onCheckedChange={toggleSelectAllWorkers}
                        data-testid="select-all-workers-checkbox"
                      />
                    </TableHead>
                    <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Cargo</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Sexo</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Email</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Teléfono</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Fecha Nac.</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Estado</TableHead>
                    <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedWorkers.map((w) => (
                    <TableRow key={w.id} className={`border-[#2B4B8C]/5 hover:bg-[#f8fafc] ${selectedWorkerIds.has(w.id) ? 'bg-red-50/50' : ''}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedWorkerIds.has(w.id)}
                          onCheckedChange={() => toggleSelectWorker(w.id)}
                          data-testid={`select-worker-${w.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{w.rut || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C] font-medium">{w.first_name} {w.last_name}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{w.cargo || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{w.sexo || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{w.email}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{w.phone || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{w.fecha_nacimiento ? new Date(w.fecha_nacimiento).toLocaleDateString('es-CL') : '-'}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${w.is_active ? 'bg-green-500/10 text-green-500 border-green-500/20 border' : 'bg-red-500/10 text-red-400 border-red-500/20 border'}`}>
                          {w.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setDeletingWorker(w)} className="text-red-400 hover:text-red-500" data-testid={`delete-worker-${w.id}`}>
                          <Trash size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredWorkers.length === 0 && (
                <div className="text-center py-12 text-[#2B4B8C]/40">
                  {searchTerm ? 'No se encontraron trabajadores' : 'No hay trabajadores asignados a esta empresa'}
                </div>
              )}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-[#2B4B8C]/10">
                  <span className="text-sm text-[#2B4B8C]/60">
                    Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredWorkers.length)} de {filteredWorkers.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="border-[#2B4B8C]/10 text-[#2B4B8C]">
                      <CaretLeft size={16} className="mr-1" /> Anterior
                    </Button>
                    <span className="text-sm text-[#2B4B8C] font-medium px-2">{currentPage} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="border-[#2B4B8C]/10 text-[#2B4B8C]">
                      Siguiente <CaretRight size={16} className="ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button
              onClick={() => { setShowAssignUser(!showAssignUser); setUserSearchTerm(''); }}
              className="bg-[#2BBAA4] text-black hover:bg-[#239986]"
              data-testid="assign-user-btn"
            >
              <Plus size={18} className="mr-2" /> Asignar Usuario
            </Button>
          </div>

          {showAssignUser && (
            <Card className="bg-[#f8fafc] border-[#2B4B8C]/10">
              <CardContent className="p-5 space-y-3">
                <p className="font-medium text-[#2B4B8C]">Seleccionar Usuario</p>
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={18} />
                  <Input
                    placeholder="Buscar por nombre, email o RUT..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="pl-9 bg-white border-[#2B4B8C]/10 text-[#2B4B8C]"
                    data-testid="search-available-users"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto rounded-md border border-[#2B4B8C]/10 bg-white">
                  {loadingAvailable ? (
                    <div className="text-center py-6 text-[#2B4B8C]/40 text-sm">Buscando...</div>
                  ) : availableUsers.length === 0 ? (
                    <div className="text-center py-6 text-[#2B4B8C]/40 text-sm">No hay usuarios disponibles</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                          <TableHead className="text-[#2B4B8C]/60 text-xs">RUT</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Nombre</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Apellido</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Email</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Rol</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs text-right">Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {availableUsers.map((u) => (
                          <TableRow key={u.id} className="border-[#2B4B8C]/5 hover:bg-[#2BBAA4]/5">
                            <TableCell className="text-[#2B4B8C]/60 text-sm py-2">{u.rut || '-'}</TableCell>
                            <TableCell className="text-[#2B4B8C] text-sm font-medium py-2">{u.first_name}</TableCell>
                            <TableCell className="text-[#2B4B8C] text-sm py-2">{u.last_name}</TableCell>
                            <TableCell className="text-[#2B4B8C]/60 text-sm py-2">{u.email}</TableCell>
                            <TableCell className="py-2">
                              <Badge className="bg-[#2B4B8C]/10 text-[#2B4B8C] border-[#2B4B8C]/20 border text-xs">
                                {u.role === 'contractor' ? 'Contratista' : u.role === 'mandante' ? 'Mandante' : u.role}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right py-2">
                              <Button
                                size="sm"
                                disabled={assigningUser}
                                onClick={() => handleAssignUser(u.id)}
                                className="bg-[#2BBAA4] text-black hover:bg-[#239986] h-7 text-xs px-3"
                                data-testid={`assign-btn-${u.id}`}
                              >
                                <Plus size={14} className="mr-1" /> Asignar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
                <Button type="button" variant="outline" onClick={() => setShowAssignUser(false)} className="border-[#2B4B8C]/10" data-testid="cancel-assign-user">Cerrar</Button>
              </CardContent>
            </Card>
          )}

          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                    <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Apellido</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Mail</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Fono</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Clave</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Rol</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Estado</TableHead>
                    <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc]">
                      <TableCell className="text-[#2B4B8C]/60">{u.rut || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C] font-medium">{u.first_name}</TableCell>
                      <TableCell className="text-[#2B4B8C]">{u.last_name}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{u.email}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{u.phone || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60 font-mono text-xs">{u.plain_password || '-'}</TableCell>
                      <TableCell>
                        <Badge className="bg-[#2B4B8C]/10 text-[#2B4B8C] border-[#2B4B8C]/20 border text-xs">
                          {u.role === 'contractor' ? 'Contratista' : u.role === 'admin' ? 'Admin' : u.role === 'mandante' ? 'Mandante' : u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${u.is_active ? 'bg-green-500/10 text-green-500 border-green-500/20 border' : 'bg-red-500/10 text-red-400 border-red-500/20 border'}`}>
                          {u.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setUnassigningUser(u)} className="text-red-400 hover:text-red-500" data-testid={`unassign-user-${u.id}`}>
                          <Trash size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {users.length === 0 && (
                <div className="text-center py-12 text-[#2B4B8C]/40">
                  No hay usuarios asignados a esta empresa
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Worker Dialog */}
      <DeleteConfirmDialog
        open={!!deletingWorker}
        onOpenChange={(open) => { if (!open) setDeletingWorker(null); }}
        entityName="Trabajador"
        itemName={deletingWorker ? `${deletingWorker.first_name} ${deletingWorker.last_name}` : ''}
        fetchDependencies={deletingWorker ? () => usersApi.getDependencies(deletingWorker.id) : null}
        onConfirmDelete={async () => {
          await usersApi.delete(deletingWorker.id);
          toast.success('Trabajador eliminado');
          setDeletingWorker(null);
          fetchUsers();
        }}
      />

      {/* Bulk Delete Workers Dialog */}
      <BulkDeleteConfirmDialog
        open={showBulkDeleteWorkers}
        onOpenChange={setShowBulkDeleteWorkers}
        entityName="trabajador(es)"
        count={selectedWorkerIds.size}
        fetchDependencies={() => usersApi.bulkDependencies([...selectedWorkerIds])}
        onConfirmDelete={async () => {
          await usersApi.bulkDelete([...selectedWorkerIds]);
          toast.success(`${selectedWorkerIds.size} trabajador(es) eliminado(s)`);
          clearWorkerSelection();
          fetchUsers();
        }}
      />

      {/* Unassign User Confirmation */}
      {unassigningUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="unassign-confirm-overlay">
          <Card className="w-96 bg-white border-[#2B4B8C]/10 shadow-lg">
            <CardContent className="p-6 space-y-4">
              <p className="font-medium text-[#2B4B8C]">Desasignar Usuario</p>
              <p className="text-sm text-[#2B4B8C]/70">
                ¿Estás seguro de desasignar a <strong>{unassigningUser.first_name} {unassigningUser.last_name}</strong> de esta empresa?
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setUnassigningUser(null)} className="border-[#2B4B8C]/10" data-testid="cancel-unassign">Cancelar</Button>
                <Button onClick={() => handleUnassignUser(unassigningUser.id)} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-unassign">Desasignar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Upload Dialog for Workers */}
      <BulkUploadDialog
        open={isBulkUploadOpen}
        onOpenChange={setIsBulkUploadOpen}
        title={`Carga Masiva - ${company.name}`}
        rutRequired={true}
        templateHeaders={['Rut', 'Nombre', 'Apellidos', 'Cargo', 'Sexo', 'Mail', 'Teléfono', 'Fecha Nacimiento']}
        fieldNames={['rut', 'first_name', 'last_name', 'cargo', 'sexo', 'email', 'phone', 'fecha_nacimiento']}
        templateExample={['12.345.678-9', 'Juan', 'Perez', 'Operador', 'M', 'trabajador@email.com', '+56912345678', '15-01-1990']}
        onUpload={async (data) => {
          const sexoMap = { 'h': 'MASCULINO', 'hombre': 'MASCULINO', 'masculino': 'MASCULINO', 'm': 'FEMENINO', 'mujer': 'FEMENINO', 'f': 'FEMENINO', 'femenino': 'FEMENINO' };
          const sexoErrors = [];
          data.forEach((row, idx) => {
            if (row.sexo && row.sexo.trim()) {
              const normalized = sexoMap[row.sexo.trim().toLowerCase()];
              if (!normalized) {
                sexoErrors.push(`Fila ${idx + 1}: Sexo inválido "${row.sexo}" (use H/Hombre/Masculino o M/Mujer/Femenino)`);
              }
            }
          });
          if (sexoErrors.length > 0) {
            toast.error(sexoErrors.slice(0, 3).join('\n') + (sexoErrors.length > 3 ? `\n...y ${sexoErrors.length - 3} más` : ''));
            throw new Error('Validación de sexo fallida');
          }
          const users = data.map(row => {
            const sexoNorm = row.sexo ? sexoMap[row.sexo.trim().toLowerCase()] || null : null;
            return {
              rut: row.rut || null, first_name: row.first_name, last_name: row.last_name,
              cargo: row.cargo || null, sexo: sexoNorm, email: row.email,
              phone: row.phone || null, fecha_nacimiento: row.fecha_nacimiento || null,
              password: row.rut ? row.rut.replace(/[^0-9kK]/g, '') : `${row.email.split('@')[0]}123`,
              role: 'worker', company_id: companyId
            };
          });
          // Check for duplicates first
          const checkRes = await usersApi.checkBulkDuplicates({ users, role: 'worker' });
          if (checkRes.data.duplicate_count > 0) {
            setPendingBulkData(users);
            setBulkDuplicates(checkRes.data);
            setIsBulkUploadOpen(false);
            return null; // Don't show success yet
          }
          const res = await usersApi.bulkUpload({ users });
          fetchUsers();
          return res.data;
        }}
        entityName="trabajadores"
      />

      <SingleDuplicateDialog
        open={!!duplicateWorker}
        onOpenChange={(open) => { if (!open) { setDuplicateWorker(null); setPendingWorkerData(null); } }}
        existingUser={duplicateWorker}
        onUpdate={handleUpdateDuplicateWorker}
        onCancel={() => { setDuplicateWorker(null); setPendingWorkerData(null); }}
      />

      <BulkDuplicateDialog
        open={!!bulkDuplicates}
        onOpenChange={(open) => { if (!open) { setBulkDuplicates(null); setPendingBulkData(null); } }}
        duplicates={bulkDuplicates?.duplicates || []}
        total={bulkDuplicates?.total || 0}
        onUpdateExisting={async () => {
          try {
            const res = await usersApi.bulkUploadUpdate({ users: pendingBulkData });
            toast.success(`${res.data.created} creados, ${res.data.updated} actualizados`);
            setBulkDuplicates(null); setPendingBulkData(null); fetchUsers();
          } catch (e) { toast.error('Error al procesar'); }
        }}
        onSkipExisting={async () => {
          try {
            const res = await usersApi.bulkUpload({ users: pendingBulkData });
            toast.success(`${res.data.created} creados, ${res.data.skipped} omitidos`);
            setBulkDuplicates(null); setPendingBulkData(null); fetchUsers();
          } catch (e) { toast.error('Error al procesar'); }
        }}
        onCancel={() => { setBulkDuplicates(null); setPendingBulkData(null); }}
      />
    </div>
  );
}
